import { AlertTriangle, ArrowLeft, Check, Download, Send, X } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchBlobUrl, get, post, toQuery } from '../api';
import { AuthImage } from '../components/AuthImage';
import { useAsk } from '../components/Ask';
import { Empty, ErrorBox, Loading, Spinner } from '../components/base';
import { formatDate, formatNumber } from '../formats';
import { moduleLabel } from '../modules';
import ContentStudio from './ContentStudio';
import Finance from './Finance';
import Inbox from './Inbox';

/**
 * Module pages. One route serves every module; the switch at the bottom picks
 * the implementation. A module the server has not activated answers 404, so
 * this page can only render for entitled users.
 */

// Labels live in ../modules so the product page and this one cannot drift.

// -- website-manager: numbers -------------------------------------------------

interface StatusCount { status: string; n: number }

interface WebsiteNumbers {
  available: boolean;
  reason?: string;
  people?: { total: number; verified: number; banned: number; sharing: number };
  captures?: StatusCount[];
  submissions?: StatusCount[];
  collection?: { models: number; entries: number; collectors: number };
  reports?: { open: number | null; total: number };
  backlog?: { oldest: string | null };
}

function NumberCard({ title, rows }: {
  title: string;
  rows: { label: string; value: string | number }[];
}) {
  return (
    <div className="card p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
        {title}
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-500 dark:text-ink-400">none</p>
      ) : (
        <dl className="space-y-1.5">
          {rows.map((row) => (
            <div key={row.label} className="flex items-baseline justify-between gap-3 text-sm">
              <dt className="text-ink-600 dark:text-paper-300">{row.label}</dt>
              <dd className="font-mono font-medium tabular-nums">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function Numbers({ productId }: { productId: string }) {
  const [numbers, setNumbers] = useState<WebsiteNumbers | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get<WebsiteNumbers>(`/modules/${productId}/website-manager/numbers`)
      .then(setNumbers)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [productId]);

  if (error) return <ErrorBox message={error} />;
  if (!numbers) return <Loading />;
  if (!numbers.available) {
    return <div className="card"><Empty title="Numbers unavailable" description={numbers.reason} /></div>;
  }

  const statusRows = (rows?: StatusCount[]) =>
    (rows ?? []).map((r) => ({ label: r.status, value: formatNumber(r.n) }));

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <NumberCard
          title="Accounts"
          rows={[
            { label: 'total', value: formatNumber(numbers.people?.total ?? 0) },
            { label: 'verified', value: formatNumber(numbers.people?.verified ?? 0) },
            { label: 'sharing enabled', value: formatNumber(numbers.people?.sharing ?? 0) },
            { label: 'banned', value: formatNumber(numbers.people?.banned ?? 0) },
          ]}
        />
        <NumberCard title="Captures" rows={statusRows(numbers.captures)} />
        <NumberCard title="Submissions" rows={statusRows(numbers.submissions)} />
        <NumberCard
          title="Collection"
          rows={[
            { label: 'models', value: formatNumber(numbers.collection?.models ?? 0) },
            { label: 'entries', value: formatNumber(numbers.collection?.entries ?? 0) },
            { label: 'collectors', value: formatNumber(numbers.collection?.collectors ?? 0) },
          ]}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <NumberCard
          title="Photo reports"
          rows={[
            { label: 'open', value: formatNumber(numbers.reports?.open ?? 0) },
            { label: 'all time', value: formatNumber(numbers.reports?.total ?? 0) },
          ]}
        />
        <NumberCard
          title="Moderation backlog"
          rows={[{
            label: 'oldest pending item',
            value: numbers.backlog?.oldest ? formatDate(numbers.backlog.oldest) : 'none - queue is clear',
          }]}
        />
      </div>
      <p className="text-xs text-ink-500 dark:text-ink-400">
        Live from the website's own database.
      </p>
    </div>
  );
}

// -- website-manager: the moderation queue ------------------------------------

interface CaptureRow {
  id: string; kind: string; created_at: string; has_people: number; consent_given: number;
  location: string | null; user_id: string | null; contact: string | null;
  handle: string | null; model_name: string; unit: string | null; rarity: string | null;
}

interface SubmissionRow {
  id: string; credit_name: string; email: string | null; location: string | null;
  story: string | null; own_rights: number; publish_requested: number;
  consent_at: string | null; created_at: string;
}

interface Counts {
  captures: number; submissions: number; reports: number; feedback: number;
  invoices: number; signups: number;
}

interface QueueData {
  captures: CaptureRow[];
  submissions: SubmissionRow[];
  counts: Counts;
  offset: number;
  per_page: number;
}

/**
 * Consent answers, shown exactly as stored and never recomputed. A yes/no
 * where TRUE is the good answer: consent given, rights confirmed.
 */
function Flag({ on, yes, no }: { on: number | boolean; yes: string; no: string }) {
  return on
    ? <span className="font-medium text-emerald-700 dark:text-emerald-400">{yes}</span>
    : <span className="font-medium text-red-700 dark:text-red-400">{no}</span>;
}

/**
 * The same shape read the other way round: here the TRUE answer is the one to
 * be careful about. `has_people` is the case that matters - coloured like a
 * Flag, "others in frame" prints green, which reads as reassurance for the
 * single riskiest fact on the card. (The standalone tool made this mistake
 * and left a note; this is that note, honoured.)
 */
function Risk({ on, yes, no }: { on: number | boolean; yes: string; no: string }) {
  return on
    ? <span className="font-medium text-amber-700 dark:text-amber-400">{yes}</span>
    : <span className="text-ink-600 dark:text-paper-300">{no}</span>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-xs">
      <dt className="w-20 shrink-0 text-ink-500 dark:text-ink-400">{label}</dt>
      <dd className="flex-1">{children}</dd>
    </div>
  );
}

function Warning({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs
                  text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

function DecisionForm({ onDecide, busy }: {
  onDecide: (decision: 'approve' | 'reject', reason: string) => void;
  busy: boolean;
}) {
  const [reason, setReason] = useState('');
  const submit = (decision: 'approve' | 'reject') => (event: FormEvent) => {
    event.preventDefault();
    onDecide(decision, reason);
  };
  return (
    <form className="mt-3 space-y-2" onSubmit={submit('approve')}>
      <textarea
        className="field min-h-[52px] text-xs"
        placeholder="Reason - required to reject, and sent to the person"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex gap-2">
        <button type="button" className="btn-danger flex-1 justify-center text-xs" disabled={busy}
          onClick={(e) => submit('reject')(e as unknown as FormEvent)}>
          <X className="h-3.5 w-3.5" aria-hidden /> Reject
        </button>
        <button type="submit" className="btn-primary flex-1 justify-center text-xs" disabled={busy}>
          {busy ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" aria-hidden />}
          Approve
        </button>
      </div>
    </form>
  );
}

function Queue({ productId }: { productId: string }) {
  const [data, setData] = useState<QueueData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const load = useCallback(() => {
    setData(null);
    get<QueueData>(`/modules/${productId}/website-manager/queue${toQuery({ offset })}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [productId, offset]);

  useEffect(load, [load]);

  async function decide(kind: 'capture' | 'submission', id: string,
    decision: 'approve' | 'reject', reason: string) {
    setBusyId(id);
    setError(null);
    try {
      const result = await post<{ note: string }>(
        `/modules/${productId}/website-manager/decide`, { kind, id, decision, reason });
      setNote(result.note);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The decision did not go through.');
    } finally {
      setBusyId(null);
    }
  }

  if (error && !data) return <ErrorBox message={error} onRetry={load} />;
  if (!data) return <Loading />;

  const total = data.counts.captures + data.counts.submissions;

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-600 dark:text-paper-300">
        {data.counts.captures} capture{data.counts.captures === 1 ? '' : 's'} and{' '}
        {data.counts.submissions} submission{data.counts.submissions === 1 ? '' : 's'} waiting.
        {data.counts.reports > 0 && ` ${data.counts.reports} open photo report${data.counts.reports === 1 ? '' : 's'}.`}
      </p>

      {note && (
        <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm
                                    text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40
                                    dark:text-emerald-200">
          {note}
        </p>
      )}
      {error && <ErrorBox message={error} />}

      {total === 0 ? (
        <div className="card">
          <Empty
            title="Nothing waiting"
            description="Every photograph has been looked at."
          />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.captures.map((row) => (
            <article key={row.id} className="card overflow-hidden">
              <AuthImage
                path={`/modules/${productId}/website-manager/photo/capture/${row.id}`}
                alt=""
                className="h-48 w-full object-cover"
              />
              <div className="p-4">
                <h3 className="mb-2 text-sm font-semibold">
                  <span className="badge mr-1.5 bg-brand-100 text-brand-900 dark:bg-brand-950 dark:text-brand-300">
                    capture
                  </span>
                  {row.model_name}
                  {row.unit && <span className="ml-1 font-normal text-ink-500">{row.unit}</span>}
                </h3>
                <dl className="space-y-1">
                  <Field label="from">
                    {row.handle ?? (
                      <>
                        <span className="font-medium text-amber-700 dark:text-amber-400">unclaimed</span>
                        {' '}{row.contact ?? 'no address'}
                      </>
                    )}
                  </Field>
                  <Field label="kind">{row.kind}</Field>
                  <Field label="people">
                    <Risk on={row.has_people} yes="others in frame" no="nobody else" />
                  </Field>
                  <Field label="consent">
                    <Flag on={row.consent_given} yes="confirmed" no="NOT confirmed" />
                  </Field>
                  {row.location && <Field label="where">{row.location}</Field>}
                  <Field label="sent">{formatDate(row.created_at)}</Field>
                </dl>
                {row.has_people === 1 && row.consent_given !== 1 && (
                  <div className="mt-3">
                    <Warning>
                      Other people are in this photograph and the uploader did <strong>not</strong>{' '}
                      confirm they agreed.
                    </Warning>
                  </div>
                )}
                {!row.user_id && (
                  <div className="mt-3">
                    <Warning>
                      Nobody has claimed this yet. Approving is still the right call if the
                      photograph is fine - it just cannot be added to a collection until
                      somebody proves the address is theirs.
                    </Warning>
                  </div>
                )}
                <DecisionForm
                  busy={busyId === row.id}
                  onDecide={(decision, reason) => decide('capture', row.id, decision, reason)}
                />
              </div>
            </article>
          ))}

          {data.submissions.map((row) => (
            <article key={row.id} className="card overflow-hidden">
              <AuthImage
                path={`/modules/${productId}/website-manager/photo/submission/${row.id}`}
                alt=""
                className="h-48 w-full object-cover"
              />
              <div className="p-4">
                <h3 className="mb-2 text-sm font-semibold">
                  <span className="badge mr-1.5 bg-paper-200 text-ink-700 dark:bg-ink-700 dark:text-paper-200">
                    submission
                  </span>
                  {row.credit_name}
                </h3>
                <dl className="space-y-1">
                  <Field label="rights">
                    <Flag on={row.own_rights} yes="confirmed" no="NOT confirmed" />
                  </Field>
                  <Field label="publish">
                    <Flag on={row.publish_requested} yes="requested" no="not requested" />
                  </Field>
                  {row.consent_at && <Field label="agreed">{formatDate(row.consent_at)}</Field>}
                  {row.location && <Field label="where">{row.location}</Field>}
                  <Field label="sent">{formatDate(row.created_at)}</Field>
                </dl>
                {row.story && (
                  <p className="mt-2 text-xs text-ink-600 dark:text-paper-300">{row.story}</p>
                )}
                {row.publish_requested !== 1 && (
                  <div className="mt-3">
                    <Warning>
                      Publication was <strong>not</strong> requested. Approving records the
                      decision; the photograph still will not appear publicly.
                    </Warning>
                  </div>
                )}
                <DecisionForm
                  busy={busyId === row.id}
                  onDecide={(decision, reason) => decide('submission', row.id, decision, reason)}
                />
              </div>
            </article>
          ))}
        </div>
      )}

      {(offset > 0 || data.captures.length + data.submissions.length >= data.per_page) && (
        <div className="flex justify-between">
          <button
            type="button" className="btn-secondary text-xs"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - data.per_page))}
          >
            Newer
          </button>
          <button
            type="button" className="btn-secondary text-xs"
            disabled={data.captures.length + data.submissions.length < data.per_page}
            onClick={() => setOffset(offset + data.per_page)}
          >
            Older
          </button>
        </div>
      )}
    </div>
  );
}

// -- website-manager: feedback ------------------------------------------------

interface FeedbackRow {
  id: string; kind: string; message: string; email: string | null; page: string | null;
  created_at: string; acknowledged_at: string | null; acknowledged_by: string | null;
  note: string | null; handle: string | null;
}

interface FeedbackData {
  feedback: FeedbackRow[]; counts: Counts; offset: number; per_page: number;
}

const KIND_STYLES: Record<string, string> = {
  problem: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300',
  criticism: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300',
  praise: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
};

function Feedback({ productId }: { productId: string }) {
  const [data, setData] = useState<FeedbackData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [offset, setOffset] = useState(0);

  const load = useCallback(() => {
    setData(null);
    get<FeedbackData>(`/modules/${productId}/website-manager/feedback${toQuery({ offset })}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [productId, offset]);

  useEffect(load, [load]);

  async function acknowledge(id: string) {
    setBusyId(id);
    setError(null);
    try {
      const result = await post<{ note: string }>(
        `/modules/${productId}/website-manager/feedback/acknowledge`,
        { id, note: drafts[id] ?? '' });
      setNote(result.note);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through.');
    } finally {
      setBusyId(null);
    }
  }

  if (error && !data) return <ErrorBox message={error} onRetry={load} />;
  if (!data) return <Loading />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-600 dark:text-paper-300">
        {data.counts.feedback === 0
          ? 'Everything has been read.'
          : `${data.counts.feedback} message${data.counts.feedback === 1 ? '' : 's'} nobody has read yet.`}
        {' '}The site promises a person reads these - marking one as read is that promise kept.
      </p>

      {note && (
        <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm
                                    text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40
                                    dark:text-emerald-200">
          {note}
        </p>
      )}
      {error && <ErrorBox message={error} />}

      {data.feedback.length === 0 ? (
        <div className="card"><Empty title="No feedback yet" description="When somebody tells the site something, it lands here." /></div>
      ) : (
        <div className="space-y-3">
          {data.feedback.map((row) => (
            <article
              key={row.id}
              className={`card p-4 ${row.acknowledged_at ? 'opacity-60' : ''}`}
            >
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className={`badge ${KIND_STYLES[row.kind] ?? 'bg-paper-200 text-ink-700 dark:bg-ink-700 dark:text-paper-200'}`}>
                  {row.kind}
                </span>
                <span className="text-xs text-ink-500 dark:text-ink-400">
                  {row.handle ?? (row.email ? row.email : 'anonymous')}
                  {row.page && ` · from ${row.page}`}
                  {` · ${formatDate(row.created_at)}`}
                </span>
              </div>
              <p className="whitespace-pre-line text-sm">{row.message}</p>

              {row.acknowledged_at ? (
                <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
                  Read by {row.acknowledged_by} on {formatDate(row.acknowledged_at)}
                  {row.note && <> · note: {row.note}</>}
                </p>
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    className="field min-w-[220px] flex-1 text-xs"
                    placeholder="Internal note, optional - never shown to the person"
                    value={drafts[row.id] ?? ''}
                    onChange={(e) => setDrafts((d) => ({ ...d, [row.id]: e.target.value }))}
                  />
                  <button
                    type="button" className="btn-primary text-xs" disabled={busyId === row.id}
                    onClick={() => acknowledge(row.id)}
                  >
                    {busyId === row.id ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" aria-hidden />}
                    Mark as read
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {(offset > 0 || data.feedback.length >= data.per_page) && (
        <div className="flex justify-between">
          <button type="button" className="btn-secondary text-xs" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - data.per_page))}>
            Newer
          </button>
          <button type="button" className="btn-secondary text-xs"
            disabled={data.feedback.length < data.per_page}
            onClick={() => setOffset(offset + data.per_page)}>
            Older
          </button>
        </div>
      )}
    </div>
  );
}

// -- website-manager: reports -------------------------------------------------

interface ReportRow {
  id: string; subject_kind: string; subject_id: string; reason: string;
  detail: string | null; created_at: string; reporter_handle: string | null;
  subject: { status: string; label: string; hidden_on_sight: boolean } | null;
}

interface ReportsData {
  reports: ReportRow[]; counts: Counts; offset: number; per_page: number;
}

/** The person-facing categories, in the moderator's language. */
const REASON_TEXT: Record<string, string> = {
  me_without_consent: 'someone in it says they did not agree to appear',
  sexual: 'sexual content',
  violent: 'violent content',
  hateful: 'hateful content',
  minor: 'appears to show a minor',
  not_glasses: 'not what the site publishes',
  other: 'other',
};

function Reports({ productId }: { productId: string }) {
  const [data, setData] = useState<ReportsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const load = useCallback(() => {
    setData(null);
    get<ReportsData>(`/modules/${productId}/website-manager/reports${toQuery({ offset })}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [productId, offset]);

  useEffect(load, [load]);

  async function resolve(id: string, outcome: 'uphold' | 'dismiss') {
    setBusyId(id);
    setError(null);
    try {
      const result = await post<{ note: string }>(
        `/modules/${productId}/website-manager/reports/resolve`, { id, outcome });
      setNote(result.note);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The resolution did not go through.');
    } finally {
      setBusyId(null);
    }
  }

  if (error && !data) return <ErrorBox message={error} onRetry={load} />;
  if (!data) return <Loading />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-600 dark:text-paper-300">
        {data.counts.reports === 0
          ? 'No open photo reports.'
          : `${data.counts.reports} open photo report${data.counts.reports === 1 ? '' : 's'}, oldest first - each one is a person waiting.`}
      </p>

      {note && (
        <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm
                                    text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40
                                    dark:text-emerald-200">
          {note}
        </p>
      )}
      {error && <ErrorBox message={error} />}

      {data.reports.length === 0 ? (
        <div className="card"><Empty title="Nothing reported" description="Reports about published photographs land here." /></div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {data.reports.map((row) => (
            <article key={row.id} className="card overflow-hidden">
              {row.subject ? (
                <AuthImage
                  path={`/modules/${productId}/website-manager/photo/${row.subject_kind}/${row.subject_id}`}
                  alt=""
                  className="h-48 w-full object-cover"
                />
              ) : (
                <div className="flex h-24 items-center justify-center bg-paper-200 text-xs text-ink-500 dark:bg-ink-700 dark:text-ink-400">
                  The photograph itself has since been deleted.
                </div>
              )}
              <div className="p-4">
                <h3 className="mb-2 text-sm font-semibold">
                  <span className="badge mr-1.5 bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300">
                    {row.reason}
                  </span>
                  {row.subject?.label ?? row.subject_id}
                </h3>
                <dl className="space-y-1">
                  <Field label="claims">{REASON_TEXT[row.reason] ?? row.reason}</Field>
                  <Field label="reported by">{row.reporter_handle ?? 'somebody not signed in'}</Field>
                  <Field label="when">{formatDate(row.created_at)}</Field>
                  {row.subject && (
                    <Field label="photo">
                      {row.subject.hidden_on_sight
                        ? <span className="font-medium text-amber-700 dark:text-amber-400">hidden on sight, awaiting this decision</span>
                        : row.subject.status === 'approved'
                          ? <span className="font-medium text-red-700 dark:text-red-400">still public</span>
                          : row.subject.status}
                    </Field>
                  )}
                </dl>
                {row.detail && (
                  <blockquote className="mt-2 border-l-2 border-paper-300 pl-2 text-xs italic
                                         text-ink-600 dark:border-ink-600 dark:text-paper-300">
                    {row.detail}
                  </blockquote>
                )}
                <div className="mt-3 flex gap-2">
                  <button type="button" className="btn-secondary flex-1 justify-center text-xs"
                    disabled={busyId === row.id} onClick={() => resolve(row.id, 'dismiss')}>
                    <X className="h-3.5 w-3.5" aria-hidden /> Dismiss
                  </button>
                  <button type="button" className="btn-danger flex-1 justify-center text-xs"
                    disabled={busyId === row.id} onClick={() => resolve(row.id, 'uphold')}>
                    {busyId === row.id ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" aria-hidden />}
                    Uphold takedown
                  </button>
                </div>
                <p className="mt-2 text-[11px] leading-snug text-ink-500 dark:text-ink-400">
                  Upholding takes the photograph down and tells the person who sent it, with the
                  reason. Dismissing restores anything the site hid on sight.
                </p>
              </div>
            </article>
          ))}
        </div>
      )}

      {(offset > 0 || data.reports.length >= data.per_page) && (
        <div className="flex justify-between">
          <button type="button" className="btn-secondary text-xs" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - data.per_page))}>
            Newer
          </button>
          <button type="button" className="btn-secondary text-xs"
            disabled={data.reports.length < data.per_page}
            onClick={() => setOffset(offset + data.per_page)}>
            Older
          </button>
        </div>
      )}
    </div>
  );
}

// -- website-manager: the waitlist --------------------------------------------

interface WaitlistRow {
  id: string; email: string; source: string | null; created_at: string;
  notified_at: string | null; removed_at: string | null;
}

interface WaitlistData {
  waitlist: WaitlistRow[];
  list: { active: number; notified: number; removed: number };
  counts: Counts; offset: number; per_page: number;
}

interface Mailing {
  id: string; subject: string; body: string; created_at: string; created_by: string | null;
  sent: number; failed: number; last_error: string | null; finished_at: string | null;
}

interface MailingsData { mailings: Mailing[]; recipients: number; configured: boolean }

/**
 * The news to the list, sent from here as the site. The rules are the API's
 * (websiteMailingSend): a subject, a body, a test to yourself if you want
 * one, and the recipient count confirmed before anything goes to the list.
 * The confirmation is a second screen rather than a browser dialog, so that
 * what is about to happen is read, not clicked through.
 */
function News({ productId, onSent }: { productId: string; onSent: () => void }) {
  const [data, setData] = useState<MailingsData | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    get<MailingsData>(`/modules/${productId}/website-manager/waitlist/mailings`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [productId]);

  useEffect(load, [load]);

  type Reply = { note: string; left: number; test?: boolean; mailing?: Mailing };

  async function run(what: string, payload: Record<string, unknown>) {
    setBusy(what);
    setError(null);
    setNote(null);
    try {
      const reply = await post<Reply>(`/modules/${productId}/website-manager/waitlist/mailing`, payload);
      setNote(reply.note);
      if (!reply.test) {
        setConfirming(false);
        if (reply.left === 0) { setSubject(''); setBody(''); }
        load();
        onSent();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through.');
    } finally {
      setBusy(null);
    }
  }

  const ready = subject.trim().length > 0 && body.trim().length > 0;
  const recipients = data?.recipients ?? 0;
  const unfinished = data?.mailings.find((m) => !m.finished_at);

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">News to the list</h3>
        <p className="text-xs text-ink-600 dark:text-paper-300">
          {data ? `${formatNumber(recipients)} address${recipients === 1 ? '' : 'es'} will get it` : ''}
        </p>
      </div>

      {data && !data.configured && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900
                      dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
          Sending is not set up: the API needs KULKID_RESEND_API_KEY, a Resend key for kulkid.me.
          Until then nothing can go out from here.
        </p>
      )}

      {confirming ? (
        <div className="space-y-3 rounded-lg border border-paper-300 bg-paper-50 p-3 dark:border-ink-600 dark:bg-ink-900">
          <p className="text-sm">
            Send <strong>{subject.trim()}</strong> to {formatNumber(recipients)} address{recipients === 1 ? '' : 'es'},
            as KÜLKID &lt;hello@kulkid.me&gt;? Each message carries its own way off the list. This cannot be taken back.
          </p>
          <div className="flex gap-2">
            <button type="button" className="btn-primary text-xs" disabled={busy !== null}
              onClick={() => run('send', { subject, body, confirm: recipients })}>
              {busy === 'send' ? <Spinner className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" aria-hidden />}
              Yes, send to {formatNumber(recipients)}
            </button>
            <button type="button" className="btn-secondary text-xs" disabled={busy !== null}
              onClick={() => setConfirming(false)}>
              Back
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <input className="field" placeholder="Subject" maxLength={150} value={subject}
            onChange={(e) => setSubject(e.target.value)} />
          <textarea className="field min-h-[140px]" maxLength={5000} value={body}
            placeholder="The message, as plain text. Blank lines make paragraphs. The sign-off and the way off the list are added for you."
            onChange={(e) => setBody(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-secondary text-xs" disabled={!ready || busy !== null || !data?.configured}
              onClick={() => run('test', { subject, body, test: true })}>
              {busy === 'test' ? <Spinner className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" aria-hidden />}
              Send me a test
            </button>
            <button type="button" className="btn-primary text-xs"
              disabled={!ready || busy !== null || !data?.configured || recipients === 0}
              onClick={() => setConfirming(true)}>
              <Send className="h-3.5 w-3.5" aria-hidden />
              Send to {formatNumber(recipients)}
            </button>
          </div>
        </div>
      )}

      {note && (
        <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm
                                    text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40
                                    dark:text-emerald-200">
          {note}
        </p>
      )}
      {error && <ErrorBox message={error} />}

      {unfinished && (
        <p className="flex flex-wrap items-center gap-2 text-xs text-ink-600 dark:text-paper-300">
          <span>
            <strong>{unfinished.subject}</strong> stopped with {unfinished.sent} sent and {unfinished.failed} refused
            {unfinished.last_error ? ` (${unfinished.last_error})` : ''}.
          </span>
          <button type="button" className="btn-secondary text-xs" disabled={busy !== null}
            onClick={() => run('continue', { mailing_id: unfinished.id })}>
            {busy === 'continue' ? <Spinner className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" aria-hidden />}
            Continue
          </button>
        </p>
      )}

      {data && data.mailings.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-ink-600 dark:text-paper-300">
            Sent before: {data.mailings.length}
          </summary>
          <ul className="mt-2 space-y-1">
            {data.mailings.map((m) => (
              <li key={m.id} className="flex flex-wrap justify-between gap-2 border-t border-paper-200 py-1 dark:border-ink-700">
                <span><strong>{m.subject}</strong>{m.created_by ? ` by ${m.created_by}` : ''}</span>
                <span className="text-ink-600 dark:text-paper-300">
                  {formatDate(m.created_at)}, {m.sent} sent{m.failed ? `, ${m.failed} failed` : ''}
                  {!m.finished_at ? ', unfinished' : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/**
 * The holding page's list: one address per person, signed up for news from
 * KÜLKID. The App shows it, sends the news (see News above), hands the list
 * over as a file, marks everyone as told after a message sent from
 * elsewhere, and removes an address on request.
 */
function Waitlist({ productId }: { productId: string }) {
  const { ask, confirm } = useAsk();
  const [data, setData] = useState<WaitlistData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const load = useCallback(() => {
    setData(null);
    get<WaitlistData>(`/modules/${productId}/website-manager/waitlist${toQuery({ offset })}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [productId, offset]);

  useEffect(load, [load]);

  /**
   * The file goes to whatever sends the launch message. Fetched with the
   * token and handed to the browser as a download, the same way a photograph
   * is fetched: an <a href> cannot carry the bearer token.
   */
  async function exportCsv() {
    setBusy('csv');
    setError(null);
    try {
      const url = await fetchBlobUrl(
        `/modules/${productId}/website-manager/waitlist.csv`, 'Could not build the file.');
      const a = document.createElement('a');
      a.href = url;
      a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build the file.');
    } finally {
      setBusy(null);
    }
  }

  async function remove(row: WaitlistRow) {
    // Optional, as it always was: the API keeps whatever is given and the
    // removal itself is the record that matters.
    const reason = await ask({
      title: `Remove ${row.email} from the list?`,
      message: 'The address stays on file as removed, so the site cannot add it again.',
      field: { label: 'Why (kept in the audit trail)' },
      confirmLabel: 'Remove',
      danger: true,
    });
    if (reason === null) return;
    setBusy(row.id);
    setError(null);
    try {
      const result = await post<{ note: string }>(
        `/modules/${productId}/website-manager/waitlist/remove`, { id: row.id, reason });
      setNote(result.note);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through.');
    } finally {
      setBusy(null);
    }
  }

  async function markNotified(waiting: number) {
    if (!await confirm({
      title: `Mark ${waiting} address${waiting === 1 ? '' : 'es'} as told?`,
      message: 'Only for a message sent from somewhere else - the news sent from here marks its own recipients.',
      confirmLabel: 'Mark as told',
    })) return;
    setBusy('notified');
    setError(null);
    try {
      const result = await post<{ note: string }>(
        `/modules/${productId}/website-manager/waitlist/notified`, {});
      setNote(result.note);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through.');
    } finally {
      setBusy(null);
    }
  }

  if (error && !data) return <ErrorBox message={error} onRetry={load} />;
  if (!data) return <Loading />;

  const waiting = data.list.active - data.list.notified;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-600 dark:text-paper-300">
          {data.list.active === 0
            ? 'Nobody on the list yet.'
            : `${formatNumber(data.list.active)} on the list`
              + (data.list.notified ? `, ${formatNumber(data.list.notified)} already told` : '')
              + (data.counts.signups ? `, ${data.counts.signups} new in the last 24 hours` : '')
              + '.'}
          {' '}They signed up for news from KÜLKID: new pairs, new machines, nothing else.
        </p>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary text-xs" disabled={busy === 'csv' || data.list.active === 0}
            onClick={exportCsv}>
            {busy === 'csv' ? <Spinner className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" aria-hidden />}
            Export CSV
          </button>
          <button type="button" className="btn-secondary text-xs" disabled={busy === 'notified' || waiting === 0}
            onClick={() => markNotified(waiting)}>
            {busy === 'notified' ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" aria-hidden />}
            Mark all as told
          </button>
        </div>
      </div>

      <News productId={productId} onSent={load} />

      {note && (
        <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm
                                    text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40
                                    dark:text-emerald-200">
          {note}
        </p>
      )}
      {error && <ErrorBox message={error} />}

      {data.waitlist.length === 0 ? (
        <div className="card"><Empty title="No addresses yet" description="When somebody leaves their email on the site, it lands here." /></div>
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">
              <tr>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">From</th>
                <th className="px-4 py-2">Signed up</th>
                <th className="px-4 py-2">Told</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.waitlist.map((row) => (
                <tr key={row.id} className={`border-t border-paper-200 dark:border-ink-700 ${row.removed_at ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-2 font-medium">
                    {row.removed_at ? <s>{row.email}</s> : row.email}
                  </td>
                  <td className="px-4 py-2 text-ink-600 dark:text-paper-300">{row.source ?? ''}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-ink-600 dark:text-paper-300">{formatDate(row.created_at)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-ink-600 dark:text-paper-300">
                    {row.removed_at ? `removed ${formatDate(row.removed_at)}`
                      : row.notified_at ? formatDate(row.notified_at) : 'not yet'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {!row.removed_at && (
                      <button type="button" className="btn-secondary text-xs" disabled={busy === row.id}
                        onClick={() => remove(row)}>
                        {busy === row.id ? <Spinner className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" aria-hidden />}
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(offset > 0 || data.waitlist.length >= data.per_page) && (
        <div className="flex justify-between">
          <button type="button" className="btn-secondary text-xs" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - data.per_page))}>
            Newer
          </button>
          <button type="button" className="btn-secondary text-xs"
            disabled={data.waitlist.length < data.per_page}
            onClick={() => setOffset(offset + data.per_page)}>
            Older
          </button>
        </div>
      )}
    </div>
  );
}

// -- website-manager: invoice requests ----------------------------------------

interface InvoiceRow {
  id: string; order_ref: string; purchased_on: string; place: string;
  entity: 'personal' | 'company'; billing_name: string; nif: string | null; address: string;
  email: string; notes: string | null; status: string; created_at: string;
  resolved_at: string | null; resolved_by: string | null; invoice_ref: string | null;
  note: string | null;
}

interface InvoicesData {
  invoices: InvoiceRow[]; counts: Counts; offset: number; per_page: number; view: 'open' | 'all';
}

/**
 * What became of each request for an invoice. The invoice itself is issued in
 * the invoicing software, which sends the document; here the number is
 * recorded against the request so "did we ever issue that one?" has an
 * answer. Nothing is sent to the buyer from this screen.
 */
function Invoices({ productId }: { productId: string }) {
  const { ask } = useAsk();
  const [data, setData] = useState<InvoicesData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [refs, setRefs] = useState<Record<string, string>>({});
  const [offset, setOffset] = useState(0);
  const [view, setView] = useState<'open' | 'all'>('open');

  const load = useCallback(() => {
    setData(null);
    get<InvoicesData>(`/modules/${productId}/website-manager/invoices${toQuery({ offset, view })}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [productId, offset, view]);

  useEffect(load, [load]);

  async function resolve(row: InvoiceRow, outcome: 'issued' | 'dismissed') {
    let body: Record<string, string> = { id: row.id, outcome };
    if (outcome === 'issued') {
      const invoiceRef = (refs[row.id] ?? '').trim();
      if (!invoiceRef) {
        setError('Type the number of the invoice you issued first.');
        return;
      }
      body = { ...body, invoice_ref: invoiceRef };
    } else {
      const reason = await ask({
        title: `Dismiss the request for ${row.order_ref}?`,
        message: 'No invoice is issued for it.',
        field: { label: 'Why (kept on the record)', required: true },
        confirmLabel: 'Dismiss',
        danger: true,
      });
      if (reason === null) return;
      body = { ...body, note: reason };
    }
    setBusyId(row.id);
    setError(null);
    try {
      const result = await post<{ note: string }>(
        `/modules/${productId}/website-manager/invoices/resolve`, body);
      setNote(result.note);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through.');
    } finally {
      setBusyId(null);
    }
  }

  if (error && !data) return <ErrorBox message={error} onRetry={load} />;
  if (!data) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-600 dark:text-paper-300">
          {data.counts.invoices === 0
            ? 'Every request has been answered.'
            : `${data.counts.invoices} request${data.counts.invoices === 1 ? '' : 's'} waiting for an invoice.`}
          {' '}Issue it in the invoicing software, then record the number here.
        </p>
        <div className="flex rounded-lg border border-paper-300 dark:border-ink-700" role="tablist">
          {(['open', 'all'] as const).map((v) => (
            <button key={v} type="button" role="tab" aria-selected={view === v}
              onClick={() => { setOffset(0); setView(v); }}
              className={`px-3 py-1.5 text-xs font-medium first:rounded-l-lg last:rounded-r-lg ${
                view === v
                  ? 'bg-ink-900 text-paper-100 dark:bg-paper-100 dark:text-ink-900'
                  : 'text-ink-600 hover:bg-paper-100 dark:text-paper-300 dark:hover:bg-ink-700'
              }`}>
              {v === 'open' ? 'Open' : 'All'}
            </button>
          ))}
        </div>
      </div>

      {note && (
        <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm
                                    text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40
                                    dark:text-emerald-200">
          {note}
        </p>
      )}
      {error && <ErrorBox message={error} />}

      {data.invoices.length === 0 ? (
        <div className="card"><Empty title={view === 'open' ? 'Nothing waiting' : 'No requests yet'}
          description="When a buyer asks the site for an invoice, it lands here." /></div>
      ) : (
        <div className="space-y-3">
          {data.invoices.map((row) => (
            <article key={row.id} className={`card p-4 ${row.status !== 'new' ? 'opacity-70' : ''}`}>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="font-semibold">{row.order_ref}</span>
                <span className={`badge ${row.entity === 'company'
                  ? 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-300'
                  : 'bg-paper-200 text-ink-700 dark:bg-ink-700 dark:text-paper-200'}`}>
                  {row.entity === 'company' ? 'company' : 'person'}
                </span>
                {row.status !== 'new' && (
                  <span className={`badge ${row.status === 'issued'
                    ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300'
                    : 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300'}`}>
                    {row.status}{row.invoice_ref ? ` ${row.invoice_ref}` : ''}
                  </span>
                )}
                <span className="text-xs text-ink-500 dark:text-ink-400">
                  bought {formatDate(row.purchased_on)} at {row.place} · asked {formatDate(row.created_at)}
                </span>
              </div>

              <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <div><dt className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">Made out to</dt><dd>{row.billing_name}</dd></div>
                <div><dt className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">NIF / VAT</dt><dd>{row.nif ?? <span className="text-ink-500 dark:text-ink-400">none given</span>}</dd></div>
                <div><dt className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">Billing address</dt><dd className="whitespace-pre-line">{row.address}</dd></div>
                <div><dt className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">Send it to</dt><dd><a className="underline" href={`mailto:${encodeURIComponent(row.email)}`}>{row.email}</a></dd></div>
                {row.notes && (
                  <div className="sm:col-span-2"><dt className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">They added</dt><dd className="whitespace-pre-line">{row.notes}</dd></div>
                )}
              </dl>

              {row.status !== 'new' ? (
                <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
                  {row.status === 'issued' ? 'Issued' : 'Dismissed'} by {row.resolved_by}
                  {row.resolved_at && ` on ${formatDate(row.resolved_at)}`}
                  {row.note && <> · {row.note}</>}
                </p>
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    className="field min-w-[200px] flex-1 text-xs"
                    placeholder="Invoice number, e.g. FT 2026/12"
                    value={refs[row.id] ?? ''}
                    onChange={(e) => setRefs((r) => ({ ...r, [row.id]: e.target.value }))}
                  />
                  <button type="button" className="btn-primary text-xs" disabled={busyId === row.id}
                    onClick={() => resolve(row, 'issued')}>
                    {busyId === row.id ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" aria-hidden />}
                    Issued
                  </button>
                  <button type="button" className="btn-secondary text-xs" disabled={busyId === row.id}
                    onClick={() => resolve(row, 'dismissed')}>
                    <X className="h-3.5 w-3.5" aria-hidden />
                    Dismiss
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {(offset > 0 || data.invoices.length >= data.per_page) && (
        <div className="flex justify-between">
          <button type="button" className="btn-secondary text-xs" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - data.per_page))}>
            Newer
          </button>
          <button type="button" className="btn-secondary text-xs"
            disabled={data.invoices.length < data.per_page}
            onClick={() => setOffset(offset + data.per_page)}>
            Older
          </button>
        </div>
      )}
    </div>
  );
}

// -- website-manager: support -------------------------------------------------

type SupportStatus = 'new' | 'in_progress' | 'resolved' | 'rejected';
type SupportOutcome = 'replacement' | 'refund' | 'repair' | 'none';
type SupportTopic = 'damaged' | 'payment' | 'question' | 'collection' | 'partnership' | 'other';

// Location and date exist only for topics 'damaged' and 'payment' (since 3 Oct
// 2026); for every other topic the site leaves them NULL. The payment reference
// and the photograph can come with any topic (the site's form since 30 Sep
// 2026), and a damaged pair always has the photograph; a payment problem does
// not need one.
interface SupportRow {
  id: string; ref: string; created_at: string; lang: string; topic: SupportTopic;
  name: string; email: string; message: string;
  location: string | null; purchase_date: string | null; payment_ref: string | null;
  has_photo: 0 | 1; content_type: string | null; bytes: number | null; notified_at: string | null;
  status: SupportStatus; outcome: SupportOutcome | null; note: string | null;
  handled_by: string | null; handled_at: string | null;
}

interface SupportData {
  requests: SupportRow[];
  counts: Record<SupportStatus, number>;
  offset: number; per_page: number; view: 'open' | 'all';
}

const SUPPORT_TOPIC_LABEL: Record<SupportTopic, string> = {
  damaged: 'Damaged pair', payment: 'Payment problem', question: 'Question', collection: 'Pair collection',
  partnership: 'Partnership', other: 'Something else',
};

const SUPPORT_STATUS_LABEL: Record<SupportStatus, string> = {
  new: 'New', in_progress: 'In progress', resolved: 'Resolved', rejected: 'Rejected',
};

const SUPPORT_OUTCOME_LABEL: Record<SupportOutcome, string> = {
  replacement: 'Replacement', refund: 'Refund', repair: 'Repair', none: 'No action',
};

const SUPPORT_BADGE: Record<SupportStatus, string> = {
  new: 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-300',
  in_progress: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300',
  resolved: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
  rejected: 'bg-paper-200 text-ink-700 dark:bg-ink-700 dark:text-paper-200',
};

interface SupportDraft { status: SupportStatus; outcome: string; note: string }

/**
 * What customers send through the site's support form, with the photograph
 * when one was sent and a place to record what was done. The site emails the
 * team when one arrives; nothing is sent to the customer from this screen.
 */
function Support({ productId }: { productId: string }) {
  const [data, setData] = useState<SupportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, SupportDraft>>({});
  const [offset, setOffset] = useState(0);
  const [view, setView] = useState<'open' | 'all'>('open');
  // The request whose original photograph is shown at full size, in place.
  const [fullId, setFullId] = useState<string | null>(null);

  const load = useCallback(() => {
    setData(null);
    get<SupportData>(`/modules/${productId}/website-manager/support${toQuery({ offset, view })}`)
      .then((d) => { setData(d); setDrafts({}); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [productId, offset, view]);

  useEffect(load, [load]);

  const draftFor = (row: SupportRow): SupportDraft =>
    drafts[row.id] ?? { status: row.status, outcome: row.outcome ?? '', note: row.note ?? '' };

  const change = (row: SupportRow, patch: Partial<SupportDraft>) =>
    setDrafts((d) => ({ ...d, [row.id]: { ...draftFor(row), ...patch } }));

  async function save(row: SupportRow) {
    const draft = draftFor(row);
    setBusyId(row.id);
    setError(null);
    try {
      const result = await post<{ note: string }>(
        `/modules/${productId}/website-manager/support/update`, { id: row.id, ...draft });
      setNote(result.note);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through.');
    } finally {
      setBusyId(null);
    }
  }

  if (error && !data) return <ErrorBox message={error} onRetry={load} />;
  if (!data) return <Loading />;

  const waiting = data.counts.new + data.counts.in_progress;
  const label = 'text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400';
  const muted = 'text-ink-500 dark:text-ink-400';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-600 dark:text-paper-300">
          {waiting === 0
            ? 'Every request has been dealt with.'
            : `${data.counts.new} new, ${data.counts.in_progress} in progress.`}
          {' '}The site emails the team as each one arrives; record here what was done.
        </p>
        <div className="flex rounded-lg border border-paper-300 dark:border-ink-700" role="tablist">
          {(['open', 'all'] as const).map((v) => (
            <button key={v} type="button" role="tab" aria-selected={view === v}
              onClick={() => { setOffset(0); setView(v); }}
              className={`px-3 py-1.5 text-xs font-medium first:rounded-l-lg last:rounded-r-lg ${
                view === v
                  ? 'bg-ink-900 text-paper-100 dark:bg-paper-100 dark:text-ink-900'
                  : 'text-ink-600 hover:bg-paper-100 dark:text-paper-300 dark:hover:bg-ink-700'
              }`}>
              {v === 'open' ? 'Open' : 'All'}
            </button>
          ))}
        </div>
      </div>

      {note && (
        <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm
                                    text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40
                                    dark:text-emerald-200">
          {note}
        </p>
      )}
      {error && <ErrorBox message={error} />}

      {data.requests.length === 0 ? (
        <div className="card"><Empty title={view === 'open' ? 'Nothing waiting' : 'No support requests yet'}
          description="When a customer writes to support from the site, it lands here." /></div>
      ) : (
        <div className="space-y-3">
          {data.requests.map((row) => {
            const draft = draftFor(row);
            const closed = row.status === 'resolved' || row.status === 'rejected';
            // A payment problem is handled like a damaged pair: it carries the
            // machine and the date, and ends in an outcome (usually a refund).
            const purchase = row.topic === 'damaged' || row.topic === 'payment';
            return (
              <article key={row.id} className={`card p-4 ${closed ? 'opacity-70' : ''}`}>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{row.ref}</span>
                  <span className={`badge ${SUPPORT_BADGE[row.status]}`}>
                    {SUPPORT_STATUS_LABEL[row.status]}
                  </span>
                  <span className="badge bg-paper-200 text-ink-700 dark:bg-ink-700 dark:text-paper-200">
                    {SUPPORT_TOPIC_LABEL[row.topic]}
                  </span>
                  <span className="text-xs text-ink-500 dark:text-ink-400">
                    received {formatDate(row.created_at)}
                  </span>
                </div>

                {/* One column while the original is shown, so it gets the card's
                    full width: squeezed into the 240px column it would show no
                    more than the smaller copy, and a hairline crack is exactly
                    what "Full size" is for. A request with no photograph has no
                    side column to reserve either. */}
                <div className={row.has_photo === 0 || fullId === row.id
                  ? 'grid gap-4'
                  : 'grid gap-4 md:grid-cols-[minmax(0,1fr)_240px]'}>
                  <div className="space-y-3">
                    <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                      <div><dt className={label}>Name</dt><dd>{row.name}</dd></div>
                      <div><dt className={label}>Email</dt><dd>
                        <a className="underline"
                          href={`mailto:${encodeURIComponent(row.email)}?subject=${encodeURIComponent(`Re: ${row.ref}`)}`}>
                          {row.email}
                        </a>
                      </dd></div>
                      {purchase && (
                        <>
                          <div><dt className={label}>Where</dt>
                            <dd>{row.location ?? <span className={muted}>not given</span>}</dd></div>
                          <div><dt className={label}>When</dt><dd>
                            {row.purchase_date ?? <span className={muted}>not given</span>}
                          </dd></div>
                          <div><dt className={label}>Payment ID</dt><dd>
                            {row.payment_ref ?? <span className={muted}>not given</span>}
                          </dd></div>
                        </>
                      )}
                      {!purchase && row.payment_ref && (
                        <div><dt className={label}>Payment ID</dt><dd>{row.payment_ref}</dd></div>
                      )}
                      <div className="sm:col-span-2"><dt className={label}>Message</dt>
                        <dd className="whitespace-pre-line">{row.message}</dd></div>
                    </dl>
                    <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                      {purchase && (
                        <div><dt className={label}>Outcome</dt>
                          <dd>{row.outcome
                            ? SUPPORT_OUTCOME_LABEL[row.outcome]
                            : <span className={muted}>not chosen</span>}</dd></div>
                      )}
                      <div><dt className={label}>Handled by</dt><dd>
                        {row.handled_by
                          ? `${row.handled_by}${row.handled_at ? ` on ${formatDate(row.handled_at)}` : ''}`
                          : <span className={muted}>nobody yet</span>}
                      </dd></div>
                      {row.note && (
                        <div className="sm:col-span-2"><dt className={label}>Note</dt>
                          <dd className="whitespace-pre-line">{row.note}</dd></div>
                      )}
                    </dl>
                  </div>

                  {row.has_photo === 1 && (
                    <div className="space-y-2">
                      {/* The original is shown here, in place, rather than in a
                          new tab: a blob opened as a page loses the photo route's
                          sandbox, and a phone blocks a tab opened after a
                          download anyway. An <img> cannot run anything. */}
                      <AuthImage
                        path={`/modules/${productId}/website-manager/photo/${fullId === row.id ? 'support-full' : 'support'}/${row.id}`}
                        alt={`Photograph sent with request ${row.ref}`}
                        className={fullId === row.id
                          ? 'w-full rounded-lg object-contain'
                          : 'h-48 w-full rounded-lg object-cover md:h-40'}
                      />
                      <button type="button" className="btn-secondary text-xs" aria-pressed={fullId === row.id}
                        onClick={() => setFullId(fullId === row.id ? null : row.id)}>
                        {fullId === row.id ? 'Smaller' : 'Full size'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <label className="text-xs">
                    <span className={label}>Status</span>
                    <select className="field mt-1 w-full text-xs" value={draft.status}
                      onChange={(e) => change(row, { status: e.target.value as SupportStatus })}>
                      {(Object.keys(SUPPORT_STATUS_LABEL) as SupportStatus[]).map((s) => (
                        <option key={s} value={s}>{SUPPORT_STATUS_LABEL[s]}</option>
                      ))}
                    </select>
                  </label>
                  {purchase && (
                    <label className="text-xs">
                      <span className={label}>Outcome</span>
                      <select className="field mt-1 w-full text-xs" value={draft.outcome}
                        onChange={(e) => change(row, { outcome: e.target.value })}>
                        <option value="">Not chosen</option>
                        {(Object.keys(SUPPORT_OUTCOME_LABEL) as SupportOutcome[]).map((o) => (
                          <option key={o} value={o}>{SUPPORT_OUTCOME_LABEL[o]}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label className="text-xs sm:col-span-2">
                    <span className={label}>Note (needed to reject)</span>
                    <textarea className="field mt-1 w-full text-xs" rows={2} maxLength={1000}
                      value={draft.note}
                      onChange={(e) => change(row, { note: e.target.value })} />
                  </label>
                </div>
                <div className="mt-2">
                  <button type="button" className="btn-primary text-xs" disabled={busyId === row.id}
                    onClick={() => save(row)}>
                    {busyId === row.id ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" aria-hidden />}
                    Save
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {(offset > 0 || data.requests.length >= data.per_page) && (
        <div className="flex justify-between">
          <button type="button" className="btn-secondary text-xs" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - data.per_page))}>
            Newer
          </button>
          <button type="button" className="btn-secondary text-xs"
            disabled={data.requests.length < data.per_page}
            onClick={() => setOffset(offset + data.per_page)}>
            Older
          </button>
        </div>
      )}
    </div>
  );
}

// -- page ---------------------------------------------------------------------

// "Photo reports", not "Reports": the tab holds people reporting photographs
// on the website, and the bare word was read as written reports about the
// product - which this module never holds (2026-09-23).
const TABS = ['Queue', 'Invoices', 'Support', 'Waitlist', 'Photo reports', 'Feedback', 'Numbers'] as const;

function WebsiteManager({ productId }: { productId: string }) {
  const [tab, setTab] = useState<typeof TABS[number]>('Queue');
  return (
    <div className="space-y-4">
      {/* Seven tabs no longer fit a phone; the bar scrolls sideways rather
          than the page, so nothing below it ever drifts off screen. */}
      <div className="flex overflow-x-auto rounded-lg border border-paper-300 dark:border-ink-700" role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`shrink-0 px-4 py-2 text-sm font-medium first:rounded-l-lg last:rounded-r-lg ${
              tab === t
                ? 'bg-ink-900 text-paper-100 dark:bg-paper-100 dark:text-ink-900'
                : 'text-ink-600 hover:bg-paper-100 dark:text-paper-300 dark:hover:bg-ink-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === 'Queue' && <Queue productId={productId} />}
      {tab === 'Invoices' && <Invoices productId={productId} />}
      {tab === 'Support' && <Support productId={productId} />}
      {tab === 'Waitlist' && <Waitlist productId={productId} />}
      {tab === 'Photo reports' && <Reports productId={productId} />}
      {tab === 'Feedback' && <Feedback productId={productId} />}
      {tab === 'Numbers' && <Numbers productId={productId} />}
    </div>
  );
}

export default function ModulePage() {
  const { id, moduleKey } = useParams();

  return (
    <div className="space-y-5">
      <div>
        <Link
          to={`/products/${id}`}
          className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-ink-500
                     hover:text-ink-700 dark:text-ink-400 dark:hover:text-paper-200"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Product
        </Link>
        <h1 className="text-2xl font-semibold">{moduleLabel(moduleKey ?? 'Module')}</h1>
      </div>

      {moduleKey === 'website-manager' && id ? <WebsiteManager productId={id} />
        : moduleKey === 'content-studio' && id ? <ContentStudio productId={id} />
          : moduleKey === 'inbox' && id ? <Inbox productId={id} />
            : moduleKey === 'finance' && id ? <Finance productId={id} />
              : <div className="card"><Empty title="Unknown module" /></div>}
    </div>
  );
}
